import type { KeyboardEvent, ReactElement } from "react"
import { useRef, useState } from "react"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import Header from "@gatsby-plugin-generic-page/components/Header"
import type { ContentTabsSectionFlatData } from "@gatsby-plugin-generic-page/fragments/contentTabsSection"
import { GenericPageSectionsRenderer } from "@gatsby-plugin-generic-page/GenericPage"
import { arrowLeft, arrowRight } from "@ionos-web-design-system/icon/system"
import { Button, Text } from "@ionos-web-design-system/react"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"

// Ensures unique ARIA IDs when multiple ContentTabsSection instances appear on the same page
let instanceCounter = 0

export default function ContentTabsSection({
  header,
  titleTypography,
  descriptionTypography,
  tabs,
  sectionSettings,
}: ContentTabsSectionFlatData): ReactElement<ContentTabsSectionFlatData> {
  const [activeTab, setActiveTab] = useState(0)
  const tabListRef = useRef<HTMLDivElement>(null)
  const instanceId = useRef(`content-tabs-${++instanceCounter}`)

  const activeTabData = tabs[activeTab]

  const handlePrev = () => setActiveTab((prev) => Math.max(0, prev - 1))
  const handleNext = () =>
    setActiveTab((prev) => Math.min(tabs.length - 1, prev + 1))

  const handleTabKeyDown = (event: KeyboardEvent, index: number) => {
    let nextIndex: number | null = null

    if (event.key === "ArrowLeft") {
      nextIndex = Math.max(0, index - 1)
    } else if (event.key === "ArrowRight") {
      nextIndex = Math.min(tabs.length - 1, index + 1)
    }

    if (nextIndex !== null && nextIndex !== index) {
      event.preventDefault()
      setActiveTab(nextIndex)

      const buttons =
        tabListRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')

      buttons?.[nextIndex]?.focus()
    }
  }

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        {!isEmpty(header) ? (
          <div className="mb-6">
            <Header {...header} />
          </div>
        ) : null}

        {/* Mobile tab navigation */}
        <div className="md:hidden mb-2">
          <div className="flex items-center justify-between mb-2">
            <Text
              variant="heading3xl"
              color="base"
              weight="bold"
              aria-live="polite"
              aria-atomic={true}
            >
              {activeTab + 1} / {tabs.length}
            </Text>

            <div className="flex gap-6">
              <Button
                onClick={handlePrev}
                concept="monochrome"
                variant="secondary"
                size="medium"
                icon={arrowLeft}
                aria-label="Previous"
                disabled={activeTab === 0}
              />

              <Button
                onClick={handleNext}
                concept="monochrome"
                variant="secondary"
                size="medium"
                icon={arrowRight}
                aria-label="Next"
                disabled={activeTab === tabs.length - 1}
              />
            </div>
          </div>

          <div className="box-border flex flex-1 flex-col gap-4 rounded-sm p-5 bg-surface-base shadow-[inset_0_calc(-1*var(--stroke-interactive))_0_0_var(--border-bolder)]">
            <Text
              variant={titleTypography ?? "headingXl"}
              weight="bold"
              color="base"
            >
              {activeTabData?.title}
            </Text>

            {!isEmpty(activeTabData?.description) ? (
              <Text variant={descriptionTypography ?? "bodySm"} color="base">
                {activeTabData.description}
              </Text>
            ) : null}
          </div>
        </div>

        {/* Desktop/tablet tab navigation row */}
        <div
          ref={tabListRef}
          className="hidden md:flex gap-2 mb-2"
          role="tablist"
        >
          {tabs.map((tab, index) => (
            <button
              key={`${tab.title}-${index}`}
              id={`${instanceId.current}-tab-${index}`}
              role="tab"
              aria-selected={index === activeTab}
              aria-controls={instanceId.current}
              tabIndex={index === activeTab ? 0 : -1}
              onClick={() => setActiveTab(index)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
              className={cn(
                "box-border flex flex-1 cursor-pointer flex-col rounded-sm p-5 text-left",
                index === activeTab
                  ? "bg-surface-subtle shadow-[inset_0_calc(-1*var(--stroke-interactive))_0_0_var(--border-bolder)]"
                  : "bg-surface-base shadow-[inset_0_0_0_var(--stroke-default)_var(--border-base)]"
              )}
            >
              <div className="flex h-full flex-col gap-3">
                <Text
                  variant={titleTypography ?? "heading2xl"}
                  weight="bold"
                  color="base"
                >
                  {tab.title}
                </Text>

                {!isEmpty(tab.description) ? (
                  <Text variant={descriptionTypography ?? "body"} color="base">
                    {tab.description}
                  </Text>
                ) : null}
              </div>
            </button>
          ))}
        </div>

        {/* Content panel */}
        <div
          id={instanceId.current}
          role="tabpanel"
          aria-labelledby={`${instanceId.current}-tab-${activeTab}`}
          className="rounded-sm bg-surface-subtle p-0.5 shadow-[inset_0_0_0_var(--stroke-default)_var(--border-base)]"
        >
          <GenericPageSectionsRenderer
            pageId={String(activeTab)}
            sections={activeTabData?.sections || []}
          />
        </div>
      </BaseContainer>
    </BaseSection>
  )
}
